import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  shoes: 'ri-footprint-line',
  'hoodies-sweaters': 'ri-t-shirt-line',
  't-shirts': 'ri-shirt-line',
  jackets: 'ri-t-shirt-air-line',
  'pants-shorts': 'ri-pantone-line',
  headwear: 'ri-sun-line',
  sets: 'ri-stack-line',
  'underwear-underpants': 'ri-checkbox-blank-circle-line',
  jersey: 'ri-team-line',
  accessories: 'ri-vip-crown-line',
  bags: 'ri-briefcase-line',
  belts: 'ri-link',
  perfumes: 'ri-drop-line',
  'other-stuff': 'ri-more-line',
};

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCategorySelect = (url: string) => {
    setIsOpen(false);
    setIsMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-lg border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-16">
          <div className="flex-shrink-0">
            <a href="/" className="block">
              <span className="text-lg md:text-xl font-bold text-foreground-950 font-heading tracking-tight">
                hipobuyspread<span className="text-primary-500">.baby</span>
              </span>
              <span className="hidden md:block text-xs text-foreground-500 mt-[-2px]">
                Independent Shopping Resource
              </span>
            </a>
          </div>

          <div className="hidden md:flex items-center gap-4 flex-1 justify-center mx-8" ref={dropdownRef}>
            <div className="relative w-full max-w-md">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-medium transition-all duration-200 hover:bg-foreground-800 cursor-pointer"
              >
                <span className="truncate">Select Category</span>
                {isOpen ? <i className="ri-arrow-up-s-line ml-2 transition-transform duration-200"></i> : <i className="ri-arrow-down-s-line ml-2 transition-transform duration-200"></i>}
              </button>
              {isOpen && (
                <div className="absolute top-full mt-2 w-full bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden z-50 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="w-full flex items-center justify-between px-5 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors duration-150 cursor-pointer text-left"
                    >
                      <span>{cat.name}</span>
                      <span className="text-xs text-foreground-500">{cat.itemCount} finds</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap px-5 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold transition-all duration-200 hover:bg-foreground-800 hover:-translate-y-0.5 cursor-pointer"
            >
              hipobuy sheet
              <i className="ri-arrow-right-up-line ml-1.5"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap px-5 py-2.5 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold transition-all duration-200 hover:bg-primary-600 hover:-translate-y-0.5 cursor-pointer"
            >
              buy it now
              <i className="ri-arrow-right-up-line ml-1.5"></i>
            </a>
          </div>

          <button
            onClick={() => {
              setIsMobileMenuOpen(!isMobileMenuOpen);
              if (isMobileMenuOpen) setIsOpen(false);
            }}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 cursor-pointer"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl w-5 h-5 flex items-center justify-center`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden pb-5 pt-2 border-t border-background-200/60">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-stack-line text-accent-600 text-sm w-4 h-4 flex items-center justify-center"></i>
              </div>
              <span className="text-sm font-semibold text-foreground-900">Browse 14 Categories</span>
              <span className="text-xs text-foreground-500 ml-auto">40K+ products</span>
            </div>

            <div className="grid grid-cols-2 gap-1.5 mb-4">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategorySelect(cat.url)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-left hover:bg-background-100 transition-colors cursor-pointer group"
                >
                  <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-50 transition-colors">
                    <i className={`${categoryIcons[cat.slug] || 'ri-price-tag-3-line'} text-foreground-600 text-sm w-4 h-4 flex items-center justify-center`}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground-800 truncate">{cat.name}</p>
                    <p className="text-[10px] text-foreground-500">{cat.itemCount} finds</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-2 pt-2 border-t border-background-200/60">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap w-full text-center px-4 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
                hipobuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap w-full text-center px-4 py-3 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-flashlight-line w-4 h-4 flex items-center justify-center"></i>
                buy it now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}